import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Download, FileSpreadsheet, FileText, Calendar, Users, DollarSign,
  BookOpen, Cake, Loader2, CheckSquare, Square, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORT_TYPES = [
  { key: 'roster', label: 'Team Roster', icon: Users, desc: 'Players with jersey, age, status', endpoint: '/api/exports/roster' },
  { key: 'roster_tryout', label: 'Try-out Roster', icon: Users, desc: 'Pre-cut player list', endpoint: '/api/exports/roster', params: '&roster_type=all' },
  { key: 'payments', label: 'Payment Records', icon: DollarSign, desc: 'All payments with status', endpoint: '/api/exports/payments' },
  { key: 'schedule', label: 'Game Schedule', icon: Calendar, desc: 'Full season schedule', endpoint: '/api/exports/schedule' },
  { key: 'gcal', label: 'Google Calendar', icon: Calendar, desc: 'Import into Google Calendar', endpoint: '/api/exports/schedule', isGcal: true },
  { key: 'families', label: 'Family Directory', icon: Users, desc: 'Family contacts by team', endpoint: '/api/exports/families' },
  { key: 'scorebook', label: 'Scorebook Stickers', icon: BookOpen, desc: 'Name + Jersey Number for scorebook', endpoint: '/api/exports/scorebook' },
  { key: 'opponent', label: 'Opponent Card', icon: BookOpen, desc: 'Opponent roster for game-day', endpoint: '/api/exports/opponent-card' },
  { key: 'tournament', label: 'Tournament Roster', icon: Users, desc: 'Name, Number, Age, Gender for tournaments', endpoint: '/api/exports/tournament' },
  { key: 'birthdays', label: 'Birthday Tracker', icon: Cake, desc: 'Player birthdays with upcoming alerts', endpoint: '/api/exports/birthdays' },
];

const ROSTER_FIELDS = ['First Name', 'Last Name', 'Birthday', 'Gender', 'Jersey #', 'Status', 'Parent Name', 'Parent Email', 'Parent Phone', 'Health Status', 'Family'];
const PAYMENT_FIELDS = ['Payment ID', 'Player', 'Team', 'Amount', 'Subtotal', 'Discount', 'Total', 'Status', 'Date', 'Paid At', 'Override'];
const SCHEDULE_FIELDS = ['Date', 'Time', 'Team', 'Opponent', 'Venue', 'Home/Away', 'Status', 'Duration (min)'];
const FAMILY_FIELDS = ['Family', 'Parent Name', 'Email', 'Phone', 'Address', 'City', 'State', 'Zip', 'Players', 'Player Count'];

export default function ExportPanel({ orgId, teams = [] }) {
  const { isDark } = useTheme();
  const [exporting, setExporting] = useState(null);
  const [showFieldPicker, setShowFieldPicker] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [birthdayData, setBirthdayData] = useState(null);
  const [format, setFormat] = useState('csv');

  const getFieldsForType = (key) => {
    if (key === 'roster' || key === 'roster_tryout') return ROSTER_FIELDS;
    if (key === 'payments') return PAYMENT_FIELDS;
    if (key === 'schedule') return SCHEDULE_FIELDS;
    if (key === 'families') return FAMILY_FIELDS;
    return [];
  };

  const toggleField = (field) => {
    setSelectedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const handleExport = async (exportType) => {
    setExporting(exportType.key);
    try {
      const isTeamLevel = ['roster', 'roster_tryout', 'scorebook', 'opponent', 'tournament'].includes(exportType.key);
      const id = isTeamLevel ? (selectedTeam || teams[0]?.id) : orgId;
      if (!id) { setExporting(null); return; }

      if (exportType.key === 'birthdays') {
        const teamParam = selectedTeam ? `?team_id=${selectedTeam}` : '';
        const { data } = await axios.get(`${API_URL}/api/exports/birthdays/${orgId}${teamParam}`);
        setBirthdayData(data);
        setExporting(null);
        return;
      }

      if (exportType.key === 'opponent') {
        const { data } = await axios.get(`${API_URL}/api/exports/opponent-card/${id}`);
        setBirthdayData({ ...data, type: 'opponent' });
        setExporting(null);
        return;
      }

      if (exportType.isGcal) {
        window.open(`${API_URL}/api/exports/schedule/${orgId}/gcal`, '_blank');
        setExporting(null);
        return;
      }

      const fieldsParam = selectedFields.length > 0 ? `&fields=${selectedFields.join(',')}` : '';
      const extraParams = exportType.params || '';
      const url = `${API_URL}${exportType.endpoint}/${id}?format=${format}${fieldsParam}${extraParams}`;

      if (format === 'json') {
        const { data } = await axios.get(url);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${exportType.key}_export.json`;
        a.click();
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Download className="h-5 w-5 text-indigo-500" />
            Data Exports
          </CardTitle>
          <div className="flex gap-3 mt-2">
            <div className="flex gap-1">
              <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Format:</Label>
              {['csv', 'json'].map(f => (
                <Badge key={f} className={`cursor-pointer ${format === f ? 'bg-indigo-600' : isDark ? 'bg-slate-600' : 'bg-slate-200'}`} onClick={() => setFormat(f)} data-testid={`format-${f}`}>
                  {f === 'csv' ? <FileSpreadsheet className="h-3 w-3 mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                  {f.toUpperCase()}
                </Badge>
              ))}
            </div>
            {teams.length > 0 && (
              <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)} className={`h-7 rounded-md px-2 border text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`} data-testid="export-team-select">
                <option value="">All teams</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {EXPORT_TYPES.map((et) => {
              const Icon = et.icon;
              const fields = getFieldsForType(et.key);
              return (
                <div key={et.key} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600 hover:bg-slate-700/50' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'} transition-colors`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
                      <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{et.label}</span>
                    </div>
                    {fields.length > 0 && (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setShowFieldPicker(et.key); setSelectedFields([...fields]); }} data-testid={`fields-${et.key}`}>
                        <Settings className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{et.desc}</p>
                  <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => handleExport(et)} disabled={exporting === et.key} data-testid={`export-${et.key}`}>
                    {exporting === et.key ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                    Export
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Birthday / Opponent inline display */}
      {birthdayData && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {birthdayData.type === 'opponent' ? <BookOpen className="h-5 w-5 text-orange-500" /> : <Cake className="h-5 w-5 text-pink-500" />}
                {birthdayData.type === 'opponent' ? `${birthdayData.team_name} - Opponent Card` : 'Birthday Tracker'}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setBirthdayData(null)}>Close</Button>
            </div>
            {birthdayData.upcoming_count !== undefined && (
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {birthdayData.upcoming_count} birthdays in the next 30 days
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(birthdayData.birthdays || birthdayData.players || []).map((item, i) => (
                <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    {item.Number !== undefined && (
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-cyan-600 text-white' : 'bg-cyan-100 text-cyan-700'}`}>
                        {item.Number || '-'}
                      </span>
                    )}
                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.Name}</span>
                  </div>
                  {item.Birthday && (
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Age {item.Age}</span>
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.Birthday}</span>
                      {item.Upcoming && <Badge className="bg-pink-600">Soon!</Badge>}
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item['Days Until']}d</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Field Picker Dialog */}
      <Dialog open={!!showFieldPicker} onOpenChange={() => setShowFieldPicker(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Select Export Fields</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Click to toggle. Order matches selection order.</p>
            {getFieldsForType(showFieldPicker).map(field => (
              <div key={field} className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer ${selectedFields.includes(field) ? (isDark ? 'bg-indigo-900/30' : 'bg-indigo-50') : ''}`} onClick={() => toggleField(field)}>
                {selectedFields.includes(field) ? <CheckSquare className="h-4 w-4 text-indigo-500" /> : <Square className="h-4 w-4 text-slate-400" />}
                <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{field}</span>
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setShowFieldPicker(null)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Label({ children, className, ...props }) {
  return <label className={`text-sm font-medium ${className}`} {...props}>{children}</label>;
}
